import { Monitor, Moon, Sun } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { useTheme } from '@/providers';

interface ThemeToggleProps {
  className?: string;
}

export default function ThemeToggle({ className }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const options = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ] as const;

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex h-7 w-7 cursor-pointer items-center justify-center rounded-md outline-none transition-all duration-200',
          'text-gray-500 hover:bg-gray-100 hover:text-gray-900',
          'dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-100'
        )}
        aria-label={`Current theme: ${theme}. Click to change.`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="relative h-4 w-4">
          {/* Sun icon - visible in light mode */}
          <Sun
            className={cn(
              'absolute inset-0 h-4 w-4 transition-all duration-300',
              resolvedTheme === 'light' ? 'rotate-0 scale-100 opacity-100' : 'rotate-90 scale-0 opacity-0'
            )}
          />
          {/* Moon icon - visible in dark mode */}
          <Moon
            className={cn(
              'absolute inset-0 h-4 w-4 transition-all duration-300',
              resolvedTheme === 'dark' ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-0 opacity-0'
            )}
          />
        </div>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute top-full right-0 mt-2 w-36 rounded-lg border p-1 shadow-lg',
            'border-gray-200 bg-white',
            'dark:border-white/10 dark:bg-gray-900'
          )}
          role="listbox"
          aria-label="Select theme"
        >
          {options.map((option) => {
            const Icon = option.icon;
            const isSelected = theme === option.value;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  setTheme(option.value);
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] outline-none transition-colors duration-150',
                  isSelected
                    ? 'bg-gray-100 font-medium text-gray-900 dark:bg-white/10 dark:text-white'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-200'
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
